import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import images from 'constants/images';
import { getCompany } from 'helpers/getCompanyVariables';
import { hasLoadedAgent, selectAgencyLogoUrl } from 'helpers/brandLogo';
import { readRememberedAgencyLogos } from 'lib/agencyLogoStorage';

const isSunshine = getCompany() === 'sunshine';

/**
 * single read point for the logo the portal should paint right now. under sunshine
 * the loaded agent's agency logo wins, then the one remembered from a previous
 * session, then the bundled brand asset. oneill always gets the bundled asset
 * @param variant - bundled slot to fall back to: 'logo', 'fullLogo' or 'fullLogoGrayScale'
 * @param tone - background the logo sits on: 'onLight' or 'onDark'. picks the
 *   artwork for both the agency logo and the bundled asset
 * @returns {{ src: string, fallbackSrc: string, isAgencyLogo: boolean, hasKnownBrand: boolean }}
 */
export default function useBrandLogo(variant = 'logo', tone = 'onLight') {
  const zohoData = useSelector(state => state.user.zohoData);
  // read once per mount. redux wins as soon as an agent is loaded, so a stale
  // value here is only ever visible on the pre-login screens
  const [remembered] = useState(readRememberedAgencyLogos);

  return useMemo(() => {
    // a dark panel needs the white-ink artwork when the brand ships one, or the
    // navy wordmark lands on navy. only sunshine has it, so oneill is unaffected
    const darkToneAsset = tone === 'onDark' ? images.brand.logoOnDark : '';
    const fallbackSrc =
      darkToneAsset || images.brand[variant] || images.brand.logo;
    // isAgentKnown separates "the agency has no logo" from "we do not know the
    // agency yet", which look the same from isAgencyLogo alone
    const isAgentKnown = hasLoadedAgent(zohoData);

    if (!isSunshine) {
      return {
        fallbackSrc,
        src: fallbackSrc,
        isAgencyLogo: false,
        hasKnownBrand: true
      };
    }

    // hasKnownBrand is false only before anyone has signed in on this browser. once
    // an agent has been seen there is a brand to paint, even if the agency has no
    // logo of its own: the bundled asset is then the right answer, not a blank space
    const base = {
      fallbackSrc,
      hasKnownBrand: isAgentKnown || remembered.hasRecord
    };

    // once a real agent is loaded their agency is authoritative, so an agent whose
    // agency has no logo never inherits the one remembered from another session
    let agencyLogo = remembered.logos[tone] ?? '';
    if (isAgentKnown) agencyLogo = selectAgencyLogoUrl(zohoData, tone);

    if (!agencyLogo) return { ...base, src: fallbackSrc, isAgencyLogo: false };
    return { ...base, src: agencyLogo, isAgencyLogo: true };
  }, [zohoData, remembered, variant, tone]);
}
